/**
 * 2026-03 · Mobile Zähler-Scanner-Assistent.
 *
 * Workflow:
 *   1. Kamera-Scan (html5-qrcode) ODER manuelle Suche
 *   2. Backend `/api/meters/scan` → 0/1/N Treffer
 *   3. Bei 1 Treffer: Meter-Karte + großes Eingabefeld + Speichern
 *   4. Nach Speichern → sofort „Nächsten Zähler scannen"
 *
 * Ergonomie: Mobile-First, große Touchflächen, numerische Tastatur bei Zahleneingabe.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { MeterCamera, openCameraStream, cameraErrorText } from "@/components/meters/MeterCamera";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, MagnifyingGlass, ArrowLeft, CheckCircle, Warning, FloppyDisk, Plus, X, Sparkle } from "@phosphor-icons/react";


export default function MeterScanner() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property_id") || null;
  const navigate = useNavigate();

  const [mode, setMode] = useState("idle"); // idle | scanning | loading | ready | saved | notfound | multiple | create
  const [manualQuery, setManualQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [extractedCode, setExtractedCode] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [camStream, setCamStream] = useState(null);
  const camStreamRef = useRef(null);
  camStreamRef.current = camStream;
  const [camPurpose, setCamPurpose] = useState("scan");
  const [ocr, setOcr] = useState(null);
  // 2026-03 — Quick-Create-State
  const [createForm, setCreateForm] = useState(null);
  // { meter_number, meter_type, location, unit_id, measurement_unit, notes,
  //   property_id, property_name, address, units:[], initial_reading_value }
  const [propSearch, setPropSearch] = useState("");
  const [propOptions, setPropOptions] = useState([]);

  // Objekt-Kontext-Fortschritt
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/meters/property/${propertyId}/progress`)
      .then((r) => setProgress(r.data))
      .catch(() => setProgress(null));
  }, [propertyId, mode === "saved"]);

  // Stream-Lebenszyklus liegt hier (nicht im MeterCamera-Effect-Cleanup):
  // React StrictMode führt Effects doppelt aus – ein Stop im Cleanup beendet die Kamera sofort.
  const stopScanner = () => setCamStream((s) => { s?.getTracks().forEach((t) => t.stop()); return null; });
  useEffect(() => () => camStreamRef.current?.getTracks().forEach((t) => t.stop()), []);

  // iPhone/Safari: getUserMedia als ERSTES await im Klick, Stream wird nicht vorab gestoppt.
  const startScanner = async (purpose = "scan") => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Kamera-API nicht verfügbar. Bitte Safari oder Chrome über https:// verwenden.", { duration: 8000 });
      return;
    }
    let stream;
    try {
      stream = await openCameraStream();
    } catch (e) {
      toast.error(cameraErrorText(e), { duration: 12000 });
      return;
    }
    if (purpose === "scan") {
      setSelectedMeter(null); setValue(""); setNotes(""); setMatches([]); setExtractedCode(""); setOcr(null);
    }
    setCamPurpose(purpose);
    setCamStream(stream);
    setMode("scanning");
  };

  const onCameraCode = async (raw) => {
    stopScanner();
    await handleCode(raw, "barcode");
  };

  const onCameraPhoto = async (dataUrl) => {
    const purpose = camPurpose;
    stopScanner();
    setMode("loading");
    try {
      const { data } = await api.post("/meters/ocr", {
        image_base64: dataUrl, property_id: propertyId || selectedMeter?.property_id || null,
        meter_id: purpose === "photo" ? selectedMeter?.meter_id : null,
      });
      const o = { ...data, preview: dataUrl };
      setOcr(o);
      const v = data.reading_value != null ? String(data.reading_value).replace(".", ",") : "";
      if (purpose === "photo") { setValue(v); setMode("ready"); }
      else if (data.count === 1) { setSelectedMeter(data.matches[0]); setValue(v); setMode("ready"); }
      else if (data.count > 1) { setExtractedCode(data.extracted || data.meter_number); setMatches(data.matches); setMode("multiple"); }
      else if (data.meter_number) { setExtractedCode(String(data.meter_number).toUpperCase()); setMode("notfound"); }
      else { toast.info("Keine Zählernummer erkannt – bitte Zähler unten suchen. Der erkannte Stand wird übernommen."); setMode("idle"); }
      if (data.reading_value == null) toast.info("Zählerstand nicht lesbar – bitte manuell eingeben.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Foto konnte nicht ausgewertet werden");
      setMode(purpose === "photo" && selectedMeter ? "ready" : "idle");
    }
  };

  const handleCode = async (raw, source) => {
    setMode("loading");
    try {
      const { data } = await api.post("/meters/scan", {
        raw_code: raw, property_id: propertyId,
      });
      setExtractedCode(data.extracted);
      if (data.count === 1) {
        setSelectedMeter(data.matches[0]);
        setMatches([]);
        setValue("");
        setMode("ready");
        toast.success(`Zähler erkannt: ${data.matches[0].meter_number}`);
      } else if (data.count > 1) {
        setMatches(data.matches);
        setMode("multiple");
      } else {
        setMatches([]);
        setMode("notfound");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan fehlgeschlagen");
      setMode("idle");
    }
  };

  const doManualSearch = async (q) => {
    setManualQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await api.get("/meters/search", {
        params: { q, property_id: propertyId, limit: 20 },
      });
      setSearchResults(data.items || []);
    } catch { setSearchResults([]); }
  };

  const pickMeter = (m) => {
    setSelectedMeter(m);
    setMatches([]);
    setSearchResults([]);
    setManualQuery("");
    setValue(ocr?.reading_value != null ? String(ocr.reading_value).replace(".", ",") : "");
    setMode("ready");
  };

  const save = async (force = false) => {
    if (!selectedMeter || !value) return;
    setBusy(true);
    try {
      await api.post(`/meters/${selectedMeter.meter_id}/reading`, {
        property_id: selectedMeter.property_id,
        value: parseFloat(String(value).replace(",", ".")),
        notes: notes || null,
        source: ocr?.photo_path ? "ocr" : extractedCode ? "barcode" : "manual",
        photo_path: ocr?.photo_path || null,
        ocr_value: ocr?.reading_value ?? null,
        force,
      });
      toast.success("Zählerstand gespeichert");
      // Progress für dasselbe Objekt laden (auch ohne property_id in URL),
      // damit weitere Zähler zur Auswahl angeboten werden können.
      try {
        const { data } = await api.get(`/meters/property/${selectedMeter.property_id}/progress`);
        setProgress(data);
      } catch { /* silent */ }
      setMode("saved");
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        if (window.confirm(`${detail.detail || "Plausibilitätswarnung"}\n\nTrotzdem speichern?`)) {
          setBusy(false);
          return save(true);
        }
      } else {
        toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      }
    } finally { setBusy(false); }
  };

  // Wenn ein Zähler aus der „andere Zähler"-Liste ausgewählt wird, füllen wir
  // aus dem Progress-Snapshot ein temporäres selectedMeter — der Rest der
  // Card-Logik erwartet dieselben Felder wie ein Scan-Treffer.
  const pickMeterFromProgress = (m) => {
    setSelectedMeter({
      meter_id: m.meter_id,
      property_id: progress.property_id,
      property_name: progress.property_name,
      address: progress.address,
      meter_number: m.meter_number,
      meter_type: m.meter_type,
      location: m.location,
      measurement_unit: m.measurement_unit || null,
      last_reading: m.last_reading || null,
    });
    setMatches([]);
    setValue("");
    setNotes("");
    setExtractedCode("");
    setMode("ready");
  };

  const reset = () => {
    setSelectedMeter(null);
    setValue("");
    setNotes("");
    setMatches([]);
    setExtractedCode("");
    setOcr(null);
    setMode("idle");
  };

  const nextMeter = () => {
    reset();
    startScanner("scan");
  };

  // -------------------- 2026-03 · Quick-Create --------------------
  const METER_TYPES = [
    { value: "electricity", label: "Strom", unit: "kWh" },
    { value: "common_electricity", label: "Allgemeinstrom", unit: "kWh" },
    { value: "gas", label: "Gas", unit: "m³" },
    { value: "cold_water", label: "Kaltwasser", unit: "m³" },
    { value: "hot_water", label: "Warmwasser", unit: "m³" },
    { value: "heat_quantity", label: "Wärmemenge", unit: "MWh" },
    { value: "district_heating", label: "Fernwärme", unit: "MWh" },
    { value: "heat_cost_allocator", label: "Heizkostenverteiler", unit: "Einh." },
    { value: "other", label: "Sonstiges", unit: "" },
  ];

  const _loadUnitsForProperty = async (pid) => {
    try {
      const { data } = await api.get("/meters/scanner/properties", { params: { q: "", limit: 100 } });
      const found = (data.items || []).find((p) => p.id === pid);
      return found?.units || [];
    } catch { return []; }
  };

  const openCreateForm = async (prefillNumber = null) => {
    // Vorbelegen: Nummer aus prefill oder extractedCode; property aus URL wenn vorhanden
    const numberPrefill = prefillNumber != null ? prefillNumber : extractedCode;
    const form = {
      meter_number: numberPrefill || "",
      meter_type: "electricity",
      location: "",
      unit_id: "",
      measurement_unit: "kWh",
      notes: "",
      property_id: propertyId || "",
      property_name: progress?.property_name || "",
      address: progress?.address || null,
      units: [],
      initial_reading_value: "",
    };
    if (propertyId) {
      form.units = await _loadUnitsForProperty(propertyId);
    }
    setCreateForm(form);
    setMode("create");
  };

  const searchPropertiesForCreate = async (q) => {
    setPropSearch(q);
    if (!q || q.length < 2) { setPropOptions([]); return; }
    try {
      const { data } = await api.get("/meters/scanner/properties", { params: { q, limit: 15 } });
      setPropOptions(data.items || []);
    } catch { setPropOptions([]); }
  };

  const pickPropertyForCreate = (p) => {
    setCreateForm((f) => ({
      ...f,
      property_id: p.id,
      property_name: p.name,
      address: p.address || null,
      units: p.units || [],
      unit_id: "",
    }));
    setPropSearch("");
    setPropOptions([]);
  };

  const setCreateType = (t) => {
    const info = METER_TYPES.find((m) => m.value === t) || {};
    setCreateForm((f) => ({
      ...f,
      meter_type: t,
      // Nur setzen wenn Nutzer noch nichts Eigenes eingegeben hat
      measurement_unit: (!f.measurement_unit || METER_TYPES.some((m) => m.unit === f.measurement_unit))
        ? (info.unit || "") : f.measurement_unit,
    }));
  };

  const submitCreate = async () => {
    if (!createForm) return;
    if (!createForm.property_id) { toast.error("Bitte Objekt wählen"); return; }
    if (!createForm.meter_number?.trim()) { toast.error("Zählernummer erforderlich"); return; }
    setBusy(true);
    try {
      const initialVal = createForm.initial_reading_value !== "" && createForm.initial_reading_value != null
        ? parseFloat(String(createForm.initial_reading_value).replace(",", "."))
        : null;
      const { data } = await api.post("/meters/scanner/quick-create", {
        property_id: createForm.property_id,
        meter_number: createForm.meter_number.trim(),
        meter_type: createForm.meter_type,
        location: createForm.location || null,
        unit_id: createForm.unit_id || null,
        measurement_unit: createForm.measurement_unit || null,
        notes: createForm.notes || null,
        initial_reading_value: initialVal,
        source: extractedCode ? "barcode" : "manual",
      });
      toast.success("Zähler angelegt");
      // Reload progress (Fortschritt)
      try {
        const pr = await api.get(`/meters/property/${data.meter.property_id}/progress`);
        setProgress(pr.data);
      } catch { /* silent */ }
      // Wenn Initialwert erfasst wurde → direkt „saved"-Ansicht
      if (data.reading) {
        setSelectedMeter(data.meter);
        setMode("saved");
      } else {
        // Sonst in ready-Modus → Nutzer kann jetzt Stand erfassen
        setSelectedMeter(data.meter);
        setValue("");
        setNotes("");
        setMode("ready");
      }
      setCreateForm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setBusy(false); }
  };
  // ----------------------------------------------------------------

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="meter-scanner-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-3 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="h-9 w-9 flex items-center justify-center rounded hover:bg-slate-100" data-testid="scanner-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">Zählerstände erfassen</div>
          {progress && (
            <div className="text-[11px] text-slate-500 truncate" data-testid="scanner-progress">
              {progress.property_name} · {progress.read_today} von {progress.total_meters} erfasst
            </div>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3 max-w-md mx-auto">
        {/* Objekt-Fortschritts-Balken */}
        {progress && (
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between text-xs text-slate-600">
              <span className="font-medium">Objekt-Begehung</span>
              <span className="tabular-nums">{progress.read_today}/{progress.total_meters}</span>
            </div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all"
                   style={{ width: `${progress.total_meters ? (progress.read_today / progress.total_meters) * 100 : 0}%` }} />
            </div>
            {progress.open > 0 && (
              <div className="text-[11px] text-amber-700 mt-1">{progress.open} Zähler noch offen</div>
            )}
          </div>
        )}

        {/* IDLE / Nach Speichern */}
        {(mode === "idle" || mode === "saved") && (
          <>
            {mode === "saved" && (
              <div className="bg-emerald-50 border-2 border-emerald-300 rounded-md p-4 flex items-start gap-3" data-testid="scanner-saved">
                <CheckCircle size={28} weight="fill" className="text-emerald-600 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">Zählerstand erfolgreich gespeichert</div>
                  <div className="text-xs text-emerald-700 mt-0.5">Bereit für den nächsten Zähler.</div>
                </div>
              </div>
            )}
            <button
              onClick={nextMeter}
              className="w-full h-16 rounded-md bg-teal-700 hover:bg-teal-800 active:bg-teal-900 text-white font-semibold text-base flex items-center justify-center gap-2 shadow-sm"
              data-testid="scanner-start-btn"
            >
              <Camera size={22} weight="bold" />
              {mode === "saved" ? "Nächsten Zähler scannen" : "Zähler scannen"}
            </button>
            <div className="text-center text-xs text-slate-500">oder manuell suchen</div>
            <div className="bg-white border border-slate-200 rounded-md p-3">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-slate-400" />
                <input
                  value={manualQuery}
                  onChange={(e) => doManualSearch(e.target.value)}
                  placeholder="Zählernummer eingeben…"
                  className="w-full h-10 pl-9 pr-3 rounded border border-slate-200 text-sm focus:outline-none focus:border-teal-500"
                  data-testid="scanner-manual-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  {searchResults.map((m) => <MeterRow key={m.meter_id} m={m} onClick={() => pickMeter(m)} />)}
                </div>
              )}
              {manualQuery.length >= 2 && searchResults.length === 0 && (
                <button
                  onClick={() => { const num = manualQuery.trim().toUpperCase(); setExtractedCode(num); openCreateForm(num); }}
                  className="mt-2 w-full h-11 rounded border-2 border-dashed border-emerald-400 hover:bg-emerald-50 text-sm text-emerald-800 font-medium flex items-center justify-center gap-2"
                  data-testid="scanner-manual-create-btn"
                >
                  <Plus size={16} weight="bold" /> „{manualQuery}" nicht gefunden — jetzt anlegen
                </button>
              )}
            </div>
            {/* 2026-03 — Weitere Zähler des Objekts nach Speichern */}
            {mode === "saved" && progress && progress.meters && progress.meters.length > 1 && (
              <div className="bg-white border border-slate-200 rounded-md" data-testid="scanner-other-meters">
                <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase text-slate-700">Weitere Zähler am Objekt</div>
                  <div className="text-[10px] text-slate-500">{progress.open} offen</div>
                </div>
                <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {(progress.meters || [])
                    .filter((m) => !m.read_today)
                    .map((m) => (
                      <button
                        key={m.meter_id}
                        onClick={() => pickMeterFromProgress(m)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start justify-between gap-2"
                        data-testid={`scanner-other-${m.meter_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-mono font-medium text-slate-900 truncate">
                            {m.meter_number}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {({heat_quantity:"Wärmemenge",hot_water:"Warmwasser",cold_water:"Kaltwasser",electricity:"Strom",gas:"Gas",heat_cost_allocator:"HKV",district_heating:"Fernwärme",common_electricity:"Allg. Strom",other:"Sonstiges"}[m.meter_type]) || m.meter_type}
                            {m.location && <> · {m.location}</>}
                          </div>
                        </div>
                        {m.last_reading && (
                          <div className="text-right shrink-0 text-[10px] text-slate-500">
                            zuletzt {m.last_reading.reading_date}
                          </div>
                        )}
                      </button>
                    ))}
                  {(progress.meters || []).filter((m) => !m.read_today).length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-slate-500">
                      Alle Zähler dieses Objekts sind heute bereits abgelesen.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* SCANNING */}
        {mode === "scanning" && camStream && (
          <MeterCamera stream={camStream} purpose={camPurpose} onCode={onCameraCode} onPhoto={onCameraPhoto}
            onClose={() => { stopScanner(); setMode(camPurpose === "photo" && selectedMeter ? "ready" : "idle"); }}
            onRestart={() => { stopScanner(); startScanner(camPurpose); }} />
        )}

        {mode === "loading" && (
          <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-600">
            {ocr === null && camPurpose === "scan" ? "Suche Zähler…" : "Foto wird ausgewertet…"}
          </div>
        )}

        {/* MULTIPLE */}
        {mode === "multiple" && (
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-sm font-semibold text-slate-900 mb-2">Mehrere Zähler mit Nummer {extractedCode}</div>
            <div className="text-xs text-slate-500 mb-3">Bitte den richtigen auswählen:</div>
            <div className="divide-y divide-slate-100">
              {matches.map((m) => <MeterRow key={m.meter_id} m={m} onClick={() => pickMeter(m)} />)}
            </div>
          </div>
        )}

        {/* NOT FOUND */}
        {mode === "notfound" && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-md p-4" data-testid="scanner-notfound">
            <div className="flex items-start gap-3">
              <Warning size={24} weight="fill" className="text-amber-600 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">Zähler nicht im System hinterlegt</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Für die erkannte Nummer <b className="font-mono">{extractedCode}</b> konnte kein Zähler gefunden werden.
                  Möchten Sie ihn jetzt anlegen?
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2">
              <button onClick={openCreateForm}
                      className="h-12 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-semibold text-sm flex items-center justify-center gap-2"
                      data-testid="scanner-create-meter-btn">
                <Plus size={18} weight="bold" /> Zähler anlegen (Nummer vorbelegt)
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={nextMeter}
                        className="h-11 rounded border border-slate-300 hover:bg-slate-50 text-sm">
                  Erneut scannen
                </button>
                <button onClick={reset}
                        className="h-11 rounded border border-slate-300 hover:bg-slate-50 text-sm">
                  Manuell suchen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CREATE — Quick-Create eines neuen Zählers */}
        {mode === "create" && createForm && (
          <div className="bg-white border-2 border-emerald-500 rounded-md shadow-sm" data-testid="scanner-create-form">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">Neuen Zähler anlegen</div>
                <div className="text-[11px] text-slate-500">Wird direkt im System hinterlegt.</div>
              </div>
              <button onClick={() => { setCreateForm(null); setMode("notfound"); }}
                      className="text-slate-400 hover:text-slate-600 h-8 w-8 flex items-center justify-center"
                      data-testid="scanner-create-cancel">
                <X size={18} />
              </button>
            </div>
            <div className="p-3 space-y-3">
              {/* Objekt (wenn nicht in URL) */}
              {!propertyId && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Objekt</label>
                  {createForm.property_id ? (
                    <div className="mt-1 p-2 rounded border-2 border-emerald-300 bg-emerald-50 flex items-start justify-between gap-2"
                         data-testid="scanner-create-property-picked">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{createForm.property_name}</div>
                        {createForm.address?.street && (
                          <div className="text-[11px] text-slate-600 truncate">
                            {createForm.address.street} {createForm.address.house_number}
                            {createForm.address.city && <>, {createForm.address.zip || ""} {createForm.address.city}</>}
                          </div>
                        )}
                      </div>
                      <button onClick={() => setCreateForm((f) => ({ ...f, property_id: "", property_name: "", address: null, units: [], unit_id: "" }))}
                              className="text-xs text-slate-500 hover:text-slate-700"
                              data-testid="scanner-create-property-clear">Ändern</button>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <input
                        value={propSearch}
                        onChange={(e) => searchPropertiesForCreate(e.target.value)}
                        placeholder="Objekt suchen (Name, Nummer, Straße, PLZ)…"
                        className="w-full h-10 px-3 rounded border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                        data-testid="scanner-create-property-search"
                      />
                      {propOptions.length > 0 && (
                        <div className="mt-1 divide-y divide-slate-100 max-h-56 overflow-y-auto border border-slate-200 rounded">
                          {propOptions.map((p) => (
                            <button key={p.id} onClick={() => pickPropertyForCreate(p)}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-50"
                                    data-testid={`scanner-create-property-opt-${p.id}`}>
                              <div className="text-sm font-medium text-slate-900 truncate">
                                {p.property_number ? `${p.property_number} · ` : ""}{p.name}
                              </div>
                              {p.address?.street && (
                                <div className="text-[11px] text-slate-500 truncate">
                                  {p.address.street} {p.address.house_number}, {p.address.zip || ""} {p.address.city || ""}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Nummer + Typ */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Zählernummer *</label>
                <input
                  value={createForm.meter_number}
                  onChange={(e) => setCreateForm((f) => ({ ...f, meter_number: e.target.value }))}
                  className="w-full h-11 mt-1 px-3 rounded border-2 border-slate-200 focus:border-emerald-500 focus:outline-none text-sm font-mono"
                  data-testid="scanner-create-number"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Zählertyp</label>
                  <select
                    value={createForm.meter_type}
                    onChange={(e) => setCreateType(e.target.value)}
                    className="w-full h-11 mt-1 px-2 rounded border border-slate-200 text-sm bg-white focus:outline-none focus:border-emerald-500"
                    data-testid="scanner-create-type"
                  >
                    {METER_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Messeinheit</label>
                  <input
                    value={createForm.measurement_unit || ""}
                    onChange={(e) => setCreateForm((f) => ({ ...f, measurement_unit: e.target.value }))}
                    placeholder="kWh / m³ / MWh…"
                    className="w-full h-11 mt-1 px-3 rounded border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                    data-testid="scanner-create-unit"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Standort (optional)</label>
                <input
                  value={createForm.location || ""}
                  onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="z. B. Keller, Wohnung 3, Zählerschrank"
                  className="w-full h-11 mt-1 px-3 rounded border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="scanner-create-location"
                />
              </div>

              {createForm.units && createForm.units.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Einheit (optional)</label>
                  <select
                    value={createForm.unit_id || ""}
                    onChange={(e) => setCreateForm((f) => ({ ...f, unit_id: e.target.value }))}
                    className="w-full h-11 mt-1 px-2 rounded border border-slate-200 text-sm bg-white focus:outline-none focus:border-emerald-500"
                    data-testid="scanner-create-unit-select"
                  >
                    <option value="">— Allgemein / nicht zugeordnet —</option>
                    {createForm.units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {[u.floor_code, u.unit_number, u.position_label].filter(Boolean).join(" · ")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  Aktueller Zählerstand (optional{createForm.measurement_unit ? `, in ${createForm.measurement_unit}` : ""})
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.001"
                  value={createForm.initial_reading_value}
                  onChange={(e) => setCreateForm((f) => ({ ...f, initial_reading_value: e.target.value }))}
                  placeholder="Kann später erfasst werden"
                  className="w-full h-14 mt-1 px-3 rounded border-2 border-slate-200 focus:border-emerald-500 focus:outline-none text-2xl font-semibold text-center tabular-nums"
                  data-testid="scanner-create-initial-value"
                />
              </div>

              <button
                onClick={submitCreate}
                disabled={busy || !createForm.property_id || !createForm.meter_number?.trim()}
                className="w-full h-14 rounded-md bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="scanner-create-submit"
              >
                <FloppyDisk size={20} weight="bold" />
                {busy ? "Legt an…" : (createForm.initial_reading_value ? "Anlegen & Stand speichern" : "Zähler anlegen")}
              </button>
            </div>
          </div>
        )}

        {/* READY - Meter-Karte + Eingabe */}
        {mode === "ready" && selectedMeter && (
          <div className="space-y-3">
            <div className="bg-white border-2 border-teal-500 rounded-md p-3 shadow-sm" data-testid="scanner-meter-card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold">
                    {({strom:"Strom",gas:"Gas",wasser:"Wasser",wasser_kalt:"Kaltwasser",wasser_warm:"Warmwasser",heizung:"Heizung",other:"Zähler"}[selectedMeter.meter_type]) || "Zähler"}
                  </div>
                  <div className="text-lg font-bold text-slate-900 font-mono truncate">
                    {selectedMeter.meter_number}
                  </div>
                  <div className="text-xs text-slate-600 mt-1 truncate">
                    {selectedMeter.property_name}
                    {selectedMeter.address?.street && <> · {selectedMeter.address.street} {selectedMeter.address.house_number}</>}
                  </div>
                  {selectedMeter.location && (
                    <div className="text-xs text-slate-500 mt-0.5">Standort: {selectedMeter.location}</div>
                  )}
                </div>
                <button onClick={reset} className="text-slate-400 hover:text-slate-600 shrink-0" data-testid="scanner-clear-btn">
                  <X size={18} />
                </button>
              </div>
              {selectedMeter.last_reading && (
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-baseline justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Letzter Stand
                  </span>
                  <div className="text-right">
                    <span className="text-base font-semibold tabular-nums text-slate-900">
                      {Number(selectedMeter.last_reading.value).toLocaleString("de-DE")}
                    </span>
                    <span className="ml-1 text-xs text-slate-600">{selectedMeter.measurement_unit}</span>
                    <div className="text-[10px] text-slate-500">{selectedMeter.last_reading.reading_date}</div>
                  </div>
                </div>
              )}
              {!selectedMeter.last_reading && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-amber-700 italic">
                  Noch keine Ablesung vorhanden — der erste Stand.
                </div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-md p-3">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Aktueller Zählerstand{selectedMeter.measurement_unit ? ` (${selectedMeter.measurement_unit})` : ""}
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                autoFocus
                className="w-full h-16 mt-1 rounded border-2 border-slate-200 focus:border-teal-500 focus:outline-none text-3xl font-bold text-center tabular-nums"
                placeholder="0"
                data-testid="scanner-value-input"
              />
              <OcrHint ocr={ocr} value={value} unit={selectedMeter.measurement_unit} />
              <button type="button" onClick={() => startScanner("photo")}
                className="w-full h-11 mt-2 rounded border border-teal-300 text-teal-800 text-sm font-medium flex items-center justify-center gap-2 hover:bg-teal-50"
                data-testid="scanner-photo-btn">
                <Camera size={16} /> {ocr?.photo_path ? "Neues Foto aufnehmen" : "Zählerstand per Foto erkennen"}
              </button>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notiz (optional)"
                className="w-full h-10 mt-2 px-3 rounded border border-slate-200 text-sm focus:outline-none focus:border-teal-500"
                data-testid="scanner-notes-input"
              />
              <button
                onClick={() => save(false)}
                disabled={busy || !value}
                className="w-full h-14 mt-3 rounded-md bg-teal-700 hover:bg-teal-800 active:bg-teal-900 text-white font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="scanner-save-btn"
              >
                <FloppyDisk size={20} weight="bold" />
                {busy ? "Speichert…" : "Zählerstand speichern"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MeterRow({ m, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left py-2 px-1 hover:bg-slate-50 flex items-start justify-between gap-2"
      data-testid={`meter-search-row-${m.meter_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium font-mono text-slate-900 truncate">{m.meter_number}</div>
        <div className="text-xs text-slate-500 truncate">
          {m.property_name}
          {m.address?.street && <> · {m.address.street}</>}
          {m.location && <> · {m.location}</>}
        </div>
      </div>
      {m.last_reading && (
        <div className="text-right shrink-0">
          <div className="text-xs tabular-nums font-medium text-slate-900">
            {Number(m.last_reading.value).toLocaleString("de-DE")} {m.measurement_unit}
          </div>
          <div className="text-[10px] text-slate-500">{m.last_reading.reading_date}</div>
        </div>
      )}
    </button>
  );
}

function OcrHint({ ocr, value, unit }) {
  if (!ocr?.photo_path && ocr?.reading_value == null) return null;
  const conf = { high: "hoch", medium: "mittel", low: "niedrig" }[ocr.confidence] || "niedrig";
  const changed = ocr.reading_value != null && String(ocr.reading_value) !== String(value).replace(",", ".");
  return (
    <div className="mt-2 flex gap-2 rounded border border-teal-200 bg-teal-50 p-2" data-testid="scanner-ocr-hint">
      {ocr.preview && <img src={ocr.preview} alt="Zählerfoto" className="h-16 w-16 rounded object-cover shrink-0" />}
      <div className="text-xs text-teal-900 min-w-0">
        <div className="font-semibold flex items-center gap-1"><Sparkle size={12} /> Aus Foto erkannt: {ocr.reading_value != null ? `${Number(ocr.reading_value).toLocaleString("de-DE", { maximumFractionDigits: 3 })} ${unit || ocr.measurement_unit || ""}` : "kein Stand lesbar"}</div>
        <div>Sicherheit: {conf}. Bitte mit der Anzeige vergleichen und bei Bedarf korrigieren.</div>
        {changed && <div className="text-amber-700" data-testid="scanner-ocr-corrected">Manuell korrigiert – das Foto wird mit gespeichert.</div>}
        {ocr.note && <div className="text-teal-700/80 italic truncate">{ocr.note}</div>}
      </div>
    </div>
  );
}
